/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import type { Locale } from "@i18n/next";
import { Link, useLocale, useTranslations } from "@i18n/next";
import { useViewedScreen } from "@packages/analytics";
import type { Pages, SearchItemType } from "@packages/sdk";
import {
  getURLFromDeeplink,
  useIsKidAccount,
  useLocalStorageState,
  useRequestMe,
  useRequestPages,
  useRequestSearch,
  useSession,
} from "@packages/sdk";
import type { ThemeType } from "@packages/ui";
import {
  Button,
  Container,
  cssMediaQueryMinWidthTablet,
  List,
  ListCategory,
  ListContent,
  ListItem,
  ListRecentSearch,
  Loader,
  numericPixels,
  SearchBar,
  spacing,
  SubscriptionIcon,
  TabGroup,
  TabGroupItem,
  Text,
  useDebouncedState,
  useDynamicViewport,
  useIntersectionObserverRefState,
  useSearchParamState,
  VideoIcon,
  zIndices,
} from "@packages/ui";
import { Section, Sections } from "@ui/sections";
import { useSearchParams } from "next/navigation";
import { useEffect, useRef, useState } from "react";

const filtersSearchItemType: {
  message: string;
  searchItemType: SearchItemType;
  value: string;
}[] = [
  {
    message: "web_search_filter_collections",
    searchItemType: "collection",
    value: "collections",
  },
  {
    message: "web_search_filter_sessions",
    searchItemType: "prayer",
    value: "sessions",
  },
  {
    message: "web_search_filter_categories",
    searchItemType: "page",
    value: "categories",
  },
];

const infiniteBufferStyles: Interpolation = {
  height: numericPixels[240],
};

const listRecentSearchButtonStyles: Interpolation = {
  border: "none",
};

const listSearchResultsStyles: Interpolation = {
  paddingTop: spacing.m,
};

const searchBarStyles: Interpolation = {
  margin: "auto",
  maxWidth: spacing.maxWidth,
};

const searchBarContainerStyles: Interpolation<ThemeType> = (
  theme: ThemeType,
) => ({
  backgroundColor: theme.semanticColors.background,
  position: "sticky",
  top: spacing.xxxl,
  transformStyle: "flat",
  zIndex: zIndices.maskOver,
  paddingTop: spacing.none,
  [cssMediaQueryMinWidthTablet]: {
    paddingTop: spacing.m,
  },
});

const searchBarContainerPaddingBottomStyles: Interpolation = {
  paddingBottom: spacing.m,
};

const searchRecentSearchesHeaderDivStyles: Interpolation = {
  alignItems: "center",
  display: "flex",
  height: spacing.xxxl,
  justifyContent: "space-between",
  padding: spacing.m,
};

const tabGroupStyles: Interpolation = {
  justifyContent: "center",
};

const tabGroupScrollAreaStyles: Interpolation = {
  maxWidth: `calc(100vw - (2 * ${spacing.l}))`,
};

const tabGroupContainerStyles: Interpolation<ThemeType> = (
  theme: ThemeType,
) => ({
  backgroundColor: theme.semanticColors.background,
  paddingBottom: spacing.m,
  paddingTop: spacing.m,
  position: "sticky",
  zIndex: zIndices.maskOver,
  top: `calc(${spacing.xxxl} + ${spacing.xxl})`,
  [cssMediaQueryMinWidthTablet]: {
    top: `calc(${spacing.xxxl} + ${spacing.m} + ${spacing.xxl})`,
  },
});

const tabGroupScrollThumbStyles: Interpolation = {
  bottom: spacing.negativeS,
};

type SearchProps = {
  pagesTrustedSearchData: Pages;
};

export const Search = ({ pagesTrustedSearchData }: SearchProps) => {
  const searchParams = useSearchParams();

  const infiniteScrollBufferRef = useRef<HTMLDivElement>(null);
  const [searchBarFocused, setSearchBarFocused] = useState(false);

  const locale = useLocale();
  const t = useTranslations();

  useViewedScreen({ screen: "search" });

  const { status } = useSession();
  const [searchDebounced, setSearchDebounced] = useDebouncedState({
    defaultValue: searchParams.get("search") ?? "",
  });
  const viewportTablet = useDynamicViewport({ minimumWidth: 768 });
  const [infiniteScrollBufferRefShowing] = useIntersectionObserverRefState({
    ref: infiniteScrollBufferRef,
  });
  const [searchRecentSearches, setSearchRecentSearches] = useLocalStorageState<
    string[]
  >({ defaultValue: [], key: "searchRecentSearches" });
  const [filterSearchParam, setFilterSearchParam] = useSearchParamState({
    defaultValue: "topResults",
    key: "filter",
    removeFromUrlIfDefaultValue: true,
  });
  const [, setSearchSearchParam] = useSearchParamState({
    defaultValue: searchDebounced,
    key: "search",
  });

  const filterSearchItemTypeActive = filtersSearchItemType.find(
    (filterSearchItemType) => filterSearchItemType.value === filterSearchParam,
  );
  const searchExists = searchDebounced.length > 0;
  const searchActiveOrExists = searchBarFocused || searchExists;

  const { isKidAccount, isLoading: isKidAccountLoading } = useIsKidAccount();
  const { query: queryMe } = useRequestMe();
  const hasSubscription = !!queryMe.data?.subscription;
  const waitingForKidAccountStatus =
    status !== "unauthenticated" && isKidAccountLoading;
  const canSearch = !waitingForKidAccountStatus;
  const { query: kidDiscoverQuery } = useRequestPages({
    enabled: !!isKidAccount,
    page: "search",
  });

  const discoverSections = isKidAccount
    ? kidDiscoverQuery.data
    : pagesTrustedSearchData;

  const { infiniteQuery: infiniteQuerySearch } = useRequestSearch({
    enabled: canSearch && searchExists,
    filters: filterSearchItemTypeActive
      ? {
          locale: locale as Locale,
          type: filterSearchItemTypeActive.searchItemType,
        }
      : { locale: locale as Locale },
    search: searchDebounced,
  });

  const handleClearRecentSearches = () => {
    setSearchRecentSearches([]);
  };

  const handleListRecentSearchClick = (recentSearch: string) => {
    setSearchDebounced(recentSearch);
  };

  const handleListSearchResultClick = () => {
    if (!searchRecentSearches?.includes(searchDebounced))
      setSearchRecentSearches([
        searchDebounced,
        ...(searchRecentSearches?.slice(0, 9) as string[]),
      ]);
  };

  const shouldFetchNextPageSearchInfiniteQuery =
    canSearch &&
    searchDebounced.length > 0 &&
    infiniteScrollBufferRefShowing &&
    infiniteQuerySearch.hasNextPage &&
    !infiniteQuerySearch.isFetchingNextPage;

  useEffect(() => {
    if (shouldFetchNextPageSearchInfiniteQuery)
      infiniteQuerySearch.fetchNextPage();
  }, [shouldFetchNextPageSearchInfiniteQuery]);

  useEffect(() => {
    setSearchSearchParam(searchDebounced);
  }, [searchDebounced]);

  useEffect(() => {
    if (searchActiveOrExists && typeof document !== "undefined") {
      if (document.scrollingElement.scrollTop > 0) {
        document.scrollingElement.scrollTo(0, 0);
      }
    }
  }, [searchActiveOrExists]);

  return (
    <Container>
      <Container paddingX css={searchBarContainerStyles}>
        <SearchBar
          disabled={!canSearch}
          placeholder={
            !viewportTablet
              ? t("web_search_placeholder_short")
              : t("web_search_placeholder")
          }
          onFocusChange={setSearchBarFocused}
          stateSetter={setSearchDebounced}
          containerCSS={[
            searchBarStyles,
            !searchExists ? searchBarContainerPaddingBottomStyles : null,
          ]}
          value={searchDebounced}
        />
      </Container>

      {canSearch && searchExists && (
        <Container maxWidth paddingX css={tabGroupContainerStyles}>
          <TabGroup
            scrollAreaCSS={[tabGroupScrollAreaStyles]}
            scrollAreaProps={{
              thumbHorizontalCSS: [tabGroupScrollThumbStyles],
            }}
            setParentState={setFilterSearchParam}
            css={tabGroupStyles}
            value={filterSearchParam}
            type="single"
          >
            <TabGroupItem parentState={filterSearchParam} value="topResults">
              {t("web_search_filter_topResults")}
            </TabGroupItem>
            {filtersSearchItemType.map((filterSearchItemType) => (
              <TabGroupItem
                key={filterSearchItemType.value}
                parentState={filterSearchParam}
                value={filterSearchItemType.value}
              >
                {t(filterSearchItemType.message)}
              </TabGroupItem>
            ))}
          </TabGroup>
        </Container>
      )}

      {waitingForKidAccountStatus && <Loader />}

      {!searchActiveOrExists && canSearch && discoverSections && (
        <Sections>
          {discoverSections.sections.map((section) => (
            <Section
              key={section.id}
              section={section}
              screenName="search"
              page={{
                id: discoverSections.id,
                slug: "search",
                title: discoverSections.name,
              }}
            />
          ))}
        </Sections>
      )}

      {canSearch && searchBarFocused && !searchExists && (
        <Container maxWidth paddingX>
          <div css={searchRecentSearchesHeaderDivStyles}>
            <Text size="m" type="title">
              {t("search_title_recent")}
            </Text>
            {searchRecentSearches?.length !== 0 && (
              <Button
                onMouseDown={handleClearRecentSearches}
                size="s"
                variant="transparentPrimary"
              >
                {t("web_words_clear")}
              </Button>
            )}
          </div>
          <List>
            {searchRecentSearches?.map((searchRecentSearch) => (
              <ListItem key={searchRecentSearch}>
                <ListRecentSearch
                  asChild
                  css={listRecentSearchButtonStyles}
                  title={searchRecentSearch}
                >
                  <button
                    onClick={() =>
                      handleListRecentSearchClick(searchRecentSearch)
                    }
                  />
                </ListRecentSearch>
              </ListItem>
            ))}
          </List>
        </Container>
      )}

      {canSearch && searchExists && (
        <Container maxWidth paddingX>
          <List css={listSearchResultsStyles}>
            {infiniteQuerySearch.data?.pages.map((page) =>
              page.results.map((result) => {
                let DeterminedListElement = null;

                switch (result.type.raw) {
                  case "collection":
                  case "prayer": {
                    const isPaid = result.is_paid.raw === "true";
                    const isLocked = isPaid && !hasSubscription;
                    const hasVideo = result.has_video?.raw === "true";
                    DeterminedListElement = (
                      <ListContent
                        asChild
                        description={result.short_desc.raw}
                        metaData={
                          result.type.raw === "collection"
                            ? [
                                result.is_music.raw === "true"
                                  ? t("web_metadata_album")
                                  : t("web_metadata_collection"),
                              ]
                            : result.length.raw
                              ? [
                                  result.is_music.raw === "true"
                                    ? t("web_metadata_song")
                                    : t("web_metadata_session"),
                                  `${result.length.raw} ${t("web_abbreviation_minutes")}`,
                                ]
                              : [
                                  result.is_music.raw === "true"
                                    ? t("web_metadata_song")
                                    : t("web_metadata_session"),
                                ]
                        }
                        metaDataPrefixIcon={
                          hasVideo ? <VideoIcon.Filled /> : undefined
                        }
                        titlePrefixIcon={
                          isLocked ? <SubscriptionIcon.Small /> : undefined
                        }
                        imageSrc={result.image_url.raw}
                        title={result.title.raw}
                      >
                        <Link
                          href={getURLFromDeeplink({
                            deeplink: result.deeplink.raw,
                          })}
                        />
                      </ListContent>
                    );
                    break;
                  }
                  case "page":
                    /** Radio not currently supported */
                    if (result.item_id.raw === 81) return null;

                    DeterminedListElement = (
                      <ListCategory
                        asChild
                        description={result.short_desc.raw || undefined}
                        imageSrc={result.image_url.raw}
                        title={result.title.raw}
                      >
                        <Link
                          href={getURLFromDeeplink({
                            deeplink: result.deeplink.raw,
                          })}
                        />
                      </ListCategory>
                    );
                    break;
                }

                if (DeterminedListElement)
                  return (
                    <ListItem
                      key={result.id.raw}
                      onClick={handleListSearchResultClick}
                    >
                      {DeterminedListElement}
                    </ListItem>
                  );

                return null;
              }),
            )}
          </List>
        </Container>
      )}

      <div ref={infiniteScrollBufferRef} css={infiniteBufferStyles} />
    </Container>
  );
};
